import type { useTranslations } from "use-intl";
import type { ActiveCall } from "@/lib/twilio/voice-context";

/**
 * Format a call duration as `m:ss` (e.g. `0:00`, `0:34`, `12:09`). Minutes
 * are never zero-padded and never roll into hours; seconds always are. Input
 * is clamped to a whole, non-negative second count, so a little clock skew on
 * a live ticker renders `0:00` rather than `-1:59`.
 *
 * Two entry points because call sites carry the duration in different units:
 * the live in-call timers diff two `Date.now()` stamps (milliseconds), while
 * the staff call bubble has already reduced the span to whole seconds. Naming
 * the unit at the boundary keeps every call site obvious and rules out the
 * ms/seconds mix-up an inline copy invites.
 */
export function formatCallDurationFromSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** As {@link formatCallDurationFromSeconds}, for a duration measured in milliseconds. */
export function formatCallDurationFromMs(ms: number): string {
  return formatCallDurationFromSeconds(ms / 1000);
}

/**
 * The one-line call status both the in-call bar and the full-screen call page
 * show: a hold first, the pre-connection states by name, then the live
 * duration once the call has a start stamp. `now` only matters on that last
 * branch, so callers tick their clock on `startedAt` alone.
 */
export function formatCallStatus(
  tLabels: ReturnType<typeof useTranslations<"labels">>,
  {
    onHold,
    startedAt,
    status,
  }: Pick<ActiveCall, "onHold" | "startedAt" | "status">,
  now: number
): string {
  if (onHold) {
    return tLabels("callStatus.onHold");
  }
  if (status !== "connected") {
    return tLabels(`callStatus.${status}`);
  }
  return startedAt
    ? formatCallDurationFromMs(now - startedAt)
    : tLabels("callStatus.connected");
}
